// v10/src/components/common/ConfirmModal.js
//
// Modal de confirmación general — único en el proyecto.
//
// API base (creada el 9/5):
//   isOpen title message confirmLabel cancelLabel
//   variant ('danger' | 'warning' | 'info')
//   onConfirm onCancel isLoading darkMode
//
// API extendida (10/5, unificación con locales de AdminAircraft y SuperAdminPanel):
//   icon         — componente lucide-react opcional. Si no se pasa, AlertTriangle.
//   iconBg       — color de fondo del círculo del ícono. Si no se pasa, accent del variant.
//   confirmColor — color sólido o gradiente del botón. Si no se pasa, gradient del variant.
//   detail       — texto secundario opcional dentro de un info box.
//   loading      — alias de isLoading (para retrocompat con call sites de los locales).
//
// Si `isOpen` no se pasa, el modal se considera abierto. Esto permite usar el
// componente con renderizado condicional externo (`{target && <ConfirmModal ... />}`)
// además del control por prop (`isOpen={true}`).

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Loader, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VARIANT_COLORS = {
  danger:  { accent: '#dc2626', accentSoft: 'rgba(239,68,68,0.1)',  accentBorder: 'rgba(239,68,68,0.2)',  gradient: 'linear-gradient(135deg,#dc2626,#ef4444)' },
  warning: { accent: '#ea580c', accentSoft: 'rgba(234,88,12,0.1)',  accentBorder: 'rgba(234,88,12,0.2)',  gradient: 'linear-gradient(135deg,#ea580c,#f97316)' },
  info:    { accent: '#3b82f6', accentSoft: 'rgba(59,130,246,0.1)', accentBorder: 'rgba(59,130,246,0.2)', gradient: 'linear-gradient(135deg,#2563eb,#3b82f6)' },
};

const isGradient = (s) => typeof s === 'string' && s.includes('gradient');

const ConfirmModal = ({
  isOpen,
  title,
  message,
  detail,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  isLoading = false,
  loading = false,
  onConfirm,
  onCancel,
  darkMode,
  icon: IconProp,
  iconBg,
  confirmColor,
}) => {
  const { t } = useTranslation('admin');

  const open = isOpen === undefined ? true : isOpen;
  const busy = isLoading || loading;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  const colors    = VARIANT_COLORS[variant] || VARIANT_COLORS.danger;
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';

  const Icon = IconProp || AlertTriangle;

  // Si confirmColor es un color sólido, también lo usamos para el ícono.
  // Si es un gradiente o no se pasa, caemos al accent del variant.
  const solidConfirm    = confirmColor && !isGradient(confirmColor) ? confirmColor : null;
  const finalIconColor  = solidConfirm || colors.accent;
  const finalIconBg     = iconBg || colors.accentSoft;
  const finalIconBorder = iconBg ? 'transparent' : colors.accentBorder;
  const finalConfirmBg  = confirmColor
    ? (isGradient(confirmColor) ? confirmColor : `linear-gradient(135deg,${confirmColor},${confirmColor})`)
    : colors.gradient;

  const resolvedConfirm = confirmLabel || t('confirm_modal_default_confirm', { defaultValue: 'Confirmar' });
  const resolvedCancel  = cancelLabel  || t('confirm_modal_default_cancel',  { defaultValue: 'Cancelar' });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
          }}
          onClick={busy ? undefined : onCancel}
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
            style={{
              width: '100%', maxWidth: 460, borderRadius: 24,
              background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
              padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -8 }}>
              <button
                type="button" onClick={onCancel} disabled={busy}
                aria-label="close"
                style={{
                  padding: 6, borderRadius: 10, border: 'none',
                  background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer',
                  color: fgMuted,
                }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <div
                style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: finalIconBg,
                  border: `2px solid ${finalIconBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px',
                }}
              >
                <Icon style={{ width: 26, height: 26, color: finalIconColor }} />
              </div>
              {title && (
                <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                  {title}
                </h3>
              )}
              {message && (
                <p style={{
                  margin: 0, fontSize: 14, color: fgMuted, lineHeight: 1.5,
                  whiteSpace: 'pre-line',
                }}>
                  {message}
                </p>
              )}
              {detail && (
                <div style={{
                  margin: '12px 0 0', padding: '10px 14px', borderRadius: 12,
                  background: colors.accentSoft,
                  border: `1px solid ${colors.accentBorder}`,
                }}>
                  <p style={{ margin: 0, fontSize: 12, color: colors.accent, fontWeight: 600 }}>
                    {detail}
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button" onClick={onCancel} disabled={busy}
                style={{
                  flex: 1, padding: 13, borderRadius: 14,
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  background: 'transparent',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 14, color: fgMuted,
                  fontFamily: 'inherit',
                }}
              >
                {resolvedCancel}
              </button>
              <motion.button
                type="button" onClick={onConfirm} disabled={busy}
                whileHover={!busy ? { scale: 1.02 } : {}}
                whileTap={!busy ? { scale: 0.97 } : {}}
                style={{
                  flex: 1, padding: 13, borderRadius: 14, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: finalConfirmBg,
                  color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <style>{`@keyframes confirm-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                {busy && (
                  <Loader style={{ width: 16, height: 16, animation: 'confirm-spin 1s linear infinite' }} />
                )}
                {resolvedConfirm}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmModal;
